import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefNoticeLook = 'briefnotice-link' | 'briefnotice-deeper' | 'briefnotice-pull' | 'briefnotice-stale';

const BRIEF_NOTICE_LOOKS = new Set<string>(['briefnotice-link', 'briefnotice-deeper', 'briefnotice-pull', 'briefnotice-stale']);
export const isWedgeBriefNoticeLook = (look: string | undefined): look is WedgeBriefNoticeLook => look !== undefined && BRIEF_NOTICE_LOOKS.has(look);

/* Released Brief status notices (the strips under the sticky header: quiet on the web, cached, saved summary, last pull, refresh overdue).
   The link is the bare accent text action at the end of a pill (12.5px bold, no box). The deeper pass is a full width inline flex box,
   44px tall, with the surface fill, an accent ink and a half pixel accent border. The pull is the full width 44px ink on page fill
   action of the saved summary card. The stale one is the small gold chip action of the refresh overdue strip. Each keeps the declaration
   order of its source and reads the exact legacy roles. None draws a transparent target or an anchor (wedgeSettingsDrawsHit): the pull
   and the deeper pass have their own 44px floor, and the link and the chip sit inside a strip that the next row follows closely, so a
   region would reach it. */
export function wedgeBriefNoticeStyle(look: WedgeBriefNoticeLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'briefnotice-link': return {
      background: 'none', border: 0, color: r('accent'),
      fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0,
      fontFamily: r('font'),
      touchAction: 'manipulation',
    };
    case 'briefnotice-deeper': return {
      marginTop: 11, width: '100%', minHeight: 44,
      background: r('surface'), color: r('accent'),
      border: `0.5px solid ${r('accent')}`, borderRadius: 11,
      fontFamily: r('font'), fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
    };
    case 'briefnotice-pull': return {
      marginTop: 12, width: '100%', minHeight: 44, borderRadius: 12,
      background: r('label'), color: r('bg'), border: 0,
      cursor: 'pointer', fontSize: 15, fontWeight: 600, fontFamily: r('font'),
      touchAction: 'manipulation',
    };
    case 'briefnotice-stale': return {
      padding: '4px 10px',
      background: r('gold'),
      border: 0,
      borderRadius: 6,
      color: r('onGold'),
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: r('font'),
      minHeight: 28,
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation',
    };
  }
}
